import clinicAPI from "../services/clinicApi";

const WRITE_BLOCK_MSG =
  "В режиме обучения это действие отключено — оно не сохраняется в ваш клуб.";

/** Method names that look like mutations (blocked unless whitelisted). */
const WRITE_NAME_RE =
  /^(create|update|delete|save|mark|send|add|upload|import|export|merge|soft|expel|complete|deactivate|purchase|init|sync|set|patch|remove|cancel|restore|transfer|assign|bulk|issue|renew|freeze|unfreeze)/i;

function blocked() {
  const err = new Error(WRITE_BLOCK_MSG);
  err.response = { data: { error: WRITE_BLOCK_MSG, training: true } };
  err.userMessage = WRITE_BLOCK_MSG;
  return Promise.reject(err);
}

function ok(data) {
  return Promise.resolve(data);
}

/**
 * Patch clinicAPI methods to use training store.
 * Whitelisted reads/writes use the store; other writes are blocked;
 * unpatched reads fall through to the real API.
 * Returns restore() to put originals back.
 */
export function patchClinicApiForTraining(store) {
  const originals = {};
  const whitelisted = new Set();

  const patch = (name, fn) => {
    if (typeof clinicAPI[name] !== "function") return;
    if (!originals[name]) originals[name] = clinicAPI[name];
    clinicAPI[name] = fn;
    whitelisted.add(name);
  };

  // ——— Reads ———
  patch("getBranches", () =>
    ok({ branches: store.getState().branches, result: 0 }),
  );
  patch("getSportDirections", () =>
    ok({ directions: store.getState().directions, result: 0 }),
  );
  patch("getSpecializations", () =>
    ok({ specializations: store.getState().specializations, result: 0 }),
  );
  patch("getSportGroupsSummary", () => {
    const groups = store.getState().specializations.map((g) => ({
      ...g,
      direction: g.direction || g.sport_direction,
      sport_direction:
        typeof g.sport_direction === "string"
          ? g.sport_direction
          : g.sport_direction?.name || g.direction?.name || null,
      members_count: store
        .getState()
        .clients.filter((c) => c.sport?.group_ids?.includes(g.id)).length,
    }));
    return ok({ groups, result: 0 });
  });
  patch("getMembershipPlans", () =>
    ok({ plans: store.getState().plans, result: 0 }),
  );
  patch("getClientMemberships", () =>
    ok({ memberships: store.getState().memberships, result: 0 }),
  );
  patch("getPatients", () => {
    const clients = store.getState().clients;
    return ok({ clients, patients: clients, total: clients.length, result: 0 });
  });
  patch("getSportClients", (params = {}) => {
    let clients = [...store.getState().clients];
    const q = String(params.q || "")
      .trim()
      .toLowerCase();
    if (q) {
      clients = clients.filter((c) =>
        String(c.full_name || "")
          .toLowerCase()
          .includes(q),
      );
    }
    return ok({ clients, total: clients.length, result: 0 });
  });
  patch("searchPatients", (q) => {
    const needle = String(q || "")
      .trim()
      .toLowerCase();
    const clients = store
      .getState()
      .clients.filter((c) =>
        String(c.full_name || "")
          .toLowerCase()
          .includes(needle),
      );
    return ok({ clients, patients: clients, result: 0 });
  });
  patch("getPatient", (id) => {
    const client = store.getState().clients.find((c) => c.id === id);
    if (!client) {
      const err = new Error("Ученик не найден");
      err.response = { data: { error: "Ученик не найден" } };
      return Promise.reject(err);
    }
    return ok({ client, patient: client, result: 0 });
  });
  patch("getSportClientProfile", (patientId) => {
    const client = store.getState().clients.find((c) => c.id === patientId);
    const memberships = store
      .getState()
      .memberships.filter((m) => m.patient_id === patientId);
    return ok({
      client,
      patient: client,
      memberships,
      sport: client?.sport || {},
      result: 0,
    });
  });
  patch("getTrainingSessions", () => {
    store.actions.ensureSession();
    return ok({ sessions: store.getState().sessions, result: 0 });
  });
  patch("getSessionJournal", (sessionId) => {
    store.actions.ensureSession();
    const st = store.getState();
    const sess =
      st.sessions.find((s) => s.id === sessionId) || st.sessions[0] || null;
    if (!sess) {
      return ok({ session: null, result: 0 });
    }
    const j = st.journals[sess.id] || { session: sess, rows: [] };
    const attendances = (j.rows || []).map((r) => ({
      patient_id: r.patient_id,
      patient: r.patient || { id: r.patient_id, full_name: r.full_name },
      status: r.status === "unknown" ? null : r.status,
      full_name: r.full_name,
      marked: r.status && r.status !== "unknown",
    }));
    const session = {
      ...sess,
      ...j.session,
      attendances,
      present_count: attendances.filter((a) => a.status === "present").length,
      marked_count: attendances.filter((a) => a.status).length,
      enrolled_count: attendances.length,
      expected_count: attendances.length,
      needs_attendance: !st.meta.attendanceMarked,
    };
    return ok({ session, rows: j.rows, journal: j.rows, result: 0 });
  });
  patch("getGroupScheduleSlots", (groupId) => {
    const slots = store.getState().scheduleSlots[groupId] || [];
    return ok({ slots, result: 0 });
  });
  patch("getGroupMembers", (groupId) => {
    const members = store
      .getState()
      .clients.filter((c) => c.sport?.group_ids?.includes(groupId));
    return ok({ members, clients: members, result: 0 });
  });
  patch("listRegistrationLinks", () =>
    ok({ links: store.getState().registrationLinks, result: 0 }),
  );
  patch("getDefaultRegistrationLink", () => {
    const link = store.getState().registrationLinks[0] || null;
    if (link) store.actions.markInviteSeen();
    return ok({ link, result: 0 });
  });
  patch("getSportToday", () => {
    store.actions.ensureSession();
    const sessions = store.getState().sessions;
    return ok({
      sessions,
      nearest_sessions: sessions,
      next_session: sessions[0] || null,
      kpi: { clients: store.getState().clients.length },
      expected_clients_today: store.getState().clients.length,
      debtors_count: 0,
      notifications: [],
      result: 0,
    });
  });
  patch("getClinicSetupStatus", () =>
    ok({
      has_direction: true,
      has_branch: true,
      has_group: store.predicates.hasGroup(),
      has_schedule: store.predicates.hasSchedule(),
      has_plan: store.predicates.hasPlan(),
      has_client: store.predicates.hasClient(),
      result: 0,
    }),
  );
  patch("syncPendingOnlineMembershipPayments", () => ok({ result: 0 }));
  patch("getOnlinePaymentAvailable", () =>
    ok({ available: false, result: 0 }),
  );
  patch("getMyAdminNotifications", () =>
    ok({ notifications: [], unread_count: 0, result: 0 }),
  );
  patch("getStaffChatInbox", () => ok({ threads: [], result: 0 }));
  patch("getDoctors", () => ok({ doctors: [], result: 0 }));
  patch("getServicesCatalog", () =>
    ok({
      specializations: store.getState().specializations,
      groups: store.getState().specializations,
      result: 0,
    }),
  );
  patch("getTenantSettings", () =>
    ok({
      tenant: {
        name: "Демо-клуб (обучение)",
        auto_approve_registrations: true,
        timezone: "Europe/Moscow",
      },
      result: 0,
    }),
  );
  patch("updateTenantSettings", (data) =>
    ok({
      tenant: {
        name: "Демо-клуб (обучение)",
        auto_approve_registrations: true,
        timezone: "Europe/Moscow",
        ...data,
      },
      result: 0,
    }),
  );

  // ——— Writes (training store only) ———
  patch("createSportDirection", (data) =>
    ok({ direction: store.actions.addDirection(data), result: 0 }),
  );
  patch("createBranch", (data) =>
    ok({ branch: store.actions.addBranch(data), result: 0 }),
  );
  patch("createSpecialization", (data) =>
    ok({ specialization: store.actions.addGroup(data), result: 0 }),
  );
  patch("updateSpecialization", (id, data) =>
    ok({ specialization: store.actions.updateGroup(id, data), result: 0 }),
  );
  patch("saveGroupScheduleSlots", (groupId, data) => {
    const slots = store.actions.saveScheduleSlots(groupId, data);
    return ok({ slots, result: 0 });
  });
  patch("createMembershipPlan", (data) =>
    ok({ plan: store.actions.addPlan(data), result: 0 }),
  );
  patch("updateMembershipPlan", (id, data) =>
    ok({ plan: store.actions.updatePlan(id, data), result: 0 }),
  );
  patch("createPatient", (data) => {
    const client = store.actions.addClient(data);
    return ok({ client, patient: client, result: 0 });
  });
  patch("createClientFamily", (data) => {
    const athletes = data.athletes || data.children || [];
    const created = (athletes.length ? athletes : [data]).map((a) =>
      store.actions.addClient({
        full_name: a.full_name || a.name || data.full_name,
        phone: a.phone || data.phone,
      }),
    );
    return ok({ clients: created, result: 0 });
  });
  patch("updatePatient", (id, data) => {
    const client = store.actions.updateClient(id, data);
    return ok({ client, patient: client, result: 0 });
  });
  patch("createClientMembership", (data) => {
    try {
      const m = store.actions.issueMembership(data);
      return ok({ membership: m, result: 0 });
    } catch (e) {
      return Promise.reject(e);
    }
  });
  patch("createClientMembershipsBulk", (data) => {
    try {
      const patientIds = data.patient_ids || [];
      const list = patientIds.map((pid) =>
        store.actions.issueMembership({ ...data, patient_id: pid }),
      );
      if (!list.length) {
        list.push(store.actions.issueMembership(data));
      }
      return ok({ memberships: list, result: 0 });
    } catch (e) {
      return Promise.reject(e);
    }
  });
  patch("createTrainingSession", (data) => {
    store.actions.ensureSession();
    const sess = {
      ...store.getState().sessions[0],
      ...data,
      id: store.getState().sessions[0]?.id,
    };
    return ok({ session: sess, result: 0 });
  });
  patch("markSessionAttendance", (sessionId, data) => {
    try {
      const marks = [];
      if (data?.patient_id) {
        marks.push({
          patient_id: data.patient_id,
          status: data.clear ? "unknown" : data.status || "present",
          present: data.status === "present",
        });
      }
      const result = store.actions.markAttendance(sessionId, {
        ...data,
        marks: marks.length ? marks : data?.marks || data?.attendance,
      });
      const st = store.getState();
      const j = st.journals[result.session.id];
      const attendances = (j?.rows || []).map((r) => ({
        patient_id: r.patient_id,
        patient: r.patient || { id: r.patient_id, full_name: r.full_name },
        status: r.status === "unknown" ? null : r.status,
        full_name: r.full_name,
      }));
      return ok({
        ...result,
        attendance: attendances[0] || null,
        session: {
          ...result.session,
          attendances,
          needs_attendance: false,
        },
        result: 0,
      });
    } catch (e) {
      return Promise.reject(e);
    }
  });
  patch("completeTrainingSession", (sessionId) => {
    try {
      store.actions.markAttendance(sessionId, {});
      return ok({ result: 0 });
    } catch (e) {
      return Promise.reject(e);
    }
  });
  patch("createRegistrationLink", (data) => {
    const link = store.actions.addRegistrationLink(data);
    return ok({ link, result: 0 });
  });
  patch("sendClientPortalInvite", () => {
    store.actions.markInviteSeen();
    return ok({ result: 0, sent: true });
  });
  patch("addGroupMember", (groupId, patientId) => {
    store.actions.addGroupMember(groupId, patientId);
    return ok({ result: 0 });
  });

  // Block any remaining mutation-looking methods not whitelisted
  for (const name of Object.keys(clinicAPI)) {
    if (whitelisted.has(name)) continue;
    if (typeof clinicAPI[name] !== "function") continue;
    if (!WRITE_NAME_RE.test(name)) continue;
    patch(name, () => blocked());
  }

  return function restoreClinicApi() {
    for (const [name, fn] of Object.entries(originals)) {
      clinicAPI[name] = fn;
    }
  };
}
